<template>
  <div class="kyc-container">
    <b-row>
      <b-col v-if="!verifyLoading && !contact.valid" cols="12">
        <div class="error-dialog d-flex align-items-center p-3 mt-0">
          <img
            src="@/assets/images/common/error-circle.svg"
            width="32"
            class="mr-3 error-image"
          />
          {{ $t('digilocker.verify.error') }}
        </div>
        <div class="spacing-16"></div>
      </b-col>
      <b-col cols="12">
        {{ stopLottie() }}
        <lottie-player
          :src="fetchLottieJSON"
          background="transparent"
          speed="1"
          style="width: 100px; height: 110px"
          :loop="lottieLoop"
          class="mb-2"
          autoplay
        ></lottie-player>
        <div class="spacing-32"></div>
      </b-col>
      <b-col cols="12" md="6">
        <div class="drip-h4 color-noble-blue-500 font-weight-600">
          {{ $t('digilocker.verify.heading') }}
        </div>
        <div class="spacing-4"></div>
        <page-header title="Know Your Customer (KYC)" />
        <div class="spacing-24"></div>
        <div class="drip-h4 font-weight-400">
          {{ $t('digilocker.verify.body1') }}
          <br />
          <br />
          {{ $t('digilocker.verify.body2') }}
        </div>
        <div class="spacing-24"></div>
        <a
          class="drip-h4 color-wealthy-green-500 font-weight-600 underline-on-hover"
          href="#"
          @click="$refs.videoModalDemo.show()"
        >
          {{ $t('digilocker.verify.link') }}
        </a>
      </b-col>
      <b-col cols="12" md="6">
        <div class="spacing-32"></div>
        <div class="spacing-8"></div>
        <img
          src="@/assets/images/common/digilocker-thumbail.png"
          class="tutorial-image w-100"
          @click="$refs.videoModalDemo.show()"
          alt="tutorial"
        />
      </b-col>
    </b-row>
    <div class="spacing-32"></div>
    <div class="spacing-32"></div>
    <b-row>
      <b-col cols="12">
        <div class="drip-body color-noble-blue-500 font-weight-600">
          {{ $t('digilocker.verify.consent.title') }}
        </div>
        <div class="spacing-8"></div>
        <div class="spacing-4"></div>
        <div class="d-flex">
          <b-form-checkbox
            v-model="consentFlag"
            size="lg"
            class="checkbox-group"
            name="kyc_consent"
            value="yes"
          >
            <div
              class="drip-h4 color-noble-blue-500 font-weight-600 cursor-pointer"
            >
              {{ $t('digilocker.verify.consent.body') }}
            </div>
          </b-form-checkbox>
        </div>
      </b-col>
      <b-col cols="12" md="4">
        <div class="spacing-32"></div>
        <action-button
          variant="solid"
          :click="submitConsent"
          :extraClass="consentFlag === 'no' || !contact.valid ? 'disabled' : ''"
        >
          <b-icon-arrow-repeat
            v-if="loader"
            class="mr-2"
            animation="spin"
            scale="0.8"
          />
          {{ $t('digilocker.verify.button') }}
          <img
            v-if="consentFlag === 'yes' && contact.valid"
            src="@/assets/images/common/chevron-right-icon.svg"
            height="14"
            class="ml-2"
          />
        </action-button>
        <div class="spacing-32"></div>
      </b-col>
    </b-row>
    <b-modal
      ref="videoModalDemo"
      id="video-modal"
      hide-header
      centered
      hide-footer
      size="xl"
      @shown="modalOpened"
      modal-class="video-modal"
      content-class="video-content"
    >
      <div class="d-flex align-items-center h-100">
        <iframe
          class="w-100"
          style="height: 400px"
          src="https://21666794.fs1.hubspotusercontent-na1.net/hubfs/21666794/Video%20Tutorials/Digilocker_Aadhar.mp4"
          title="digilocker tutorial"
          frameborder="0"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
          referrerpolicy="strict-origin-when-cross-origin"
          allowfullscreen
        ></iframe>
      </div>
    </b-modal>
  </div>
</template>
<script>
import PageHeader from '@/components/common/PageHeader'
import ActionButton from '@/components/common/ActionButton'
import digilockerService from '@/services/digilocker'
import DigitalScanner from '@/assets/images/lottie/digitalScanner.json'
import { captureException, setTags } from '@sentry/vue'

export default {
  components: { PageHeader, ActionButton },
  data() {
    return {
      openVideoModal: false,
      loader: false,
      verifyLoading: true,
      consentFlag: 'no',
      contact: {
        valid: false,
        id: 0,
        email: ''
      },
      token: '',
      DigitalScannerJSON: DigitalScanner,
      lottieLoop: true
    }
  },
  mounted() {
    this.$mpTrack('Digilocker - Verify page mounted')
    if (this.$route.query.token) {
      this.token = this.$route.query.token
      digilockerService
        .verifyToken(this.$route.query.token)
        .then((res) => {
          if (res.data?.status === 'success') {
            this.$mpTrack('Digilocker - Token Verified successfully', {
              data: res.data
            })
            this.contact = {
              valid: true,
              ...res.data?.contact
            }
          } else {
            this.$mpTrack('Digilocker - Token Verified failed', {
              data: res.data
            })
            this.contact.valid = false
          }
        })
        .catch((e) => {
          console.log(e)
          this.$mpTrack('Digilocker - Token Verified failed', {
            error: e?.response?.data || e
          })
          this.$notify({
            title: 'Failed to verify token!',
            type: 'error'
          })
        })
        .finally(() => {
          this.verifyLoading = false
        })
    } else {
      this.verifyLoading = false
      this.contact.valid = false
      this.$mpTrack('Digilocker - Token not found')
    }
  },
  computed: {
    fetchLottieJSON() {
      return JSON.stringify(this.DigitalScannerJSON)
    }
  },
  methods: {
    modalOpened() {
      this.$mpTrack('Digilocker - Tutorial video opened')
    },
    stopLottie() {
      setTimeout(() => {
        this.lottieLoop = false
      }, 15000)
    },
    submitConsent() {
      this.$mpTrack('Digilocker - Submit button clicked')
      this.loader = true
      if (this.consentFlag === 'yes' && this.token && this.contact.id) {
        digilockerService
          .submitConsent(this.contact.id, this.token)
          .then((res) => {
            if (res.data?.status === 'success' && res.data?.link) {
              this.$mpTrack('Digilocker - Digilocker link generated', {
                contact: this.contact
              })
              window.location.href = res.data.link
              this.loader = false
            } else {
              this.$notify({
                title: res.data?.message || 'Failed to generate link',
                type: 'error'
              })
              this.$mpTrack('Digilocker - Digilocker link failed generated', {
                contact: this.contact
              })
              this.loader = false
              setTags({
                contactId: this.contact.id,
                token: this.token.split(0, 8) + '...',
                link: ''
              })
              captureException(new Error('KYC: Failed to generate link'))
            }
          })
          .catch((e) => {
            this.loader = false
            console.log(e)
            this.$mpTrack('Digilocker - Digilocker link failed generated', {
              contact: this.contact,
              error: e?.response?.data || e
            })
            this.$notify({
              title: this.$$t('error.unableToProcess'),
              type: 'error'
            })
          })
      } else {
        this.loader = false
        this.$notify({
          title: 'Invalid consent',
          type: 'error'
        })
        this.$mpTrack('Digilocker - Invalid consent', {
          contact: this.contact,
          token: this.token,
          consentFlag: this.consentFlag
        })
      }
    }
  },
  watch: {
    consentFlag(newVal) {
      if (newVal) {
        this.$mpTrack('Digilocker - consent checkbox clicked')
      }
    }
  }
}
</script>
<style lang="scss" scoped>
@import '@/assets/scss/_variables.scss';

.kyc-container {
  width: 100%;
  padding: 40px 108px;
  margin-bottom: 8px;
  @media screen and (max-width: 1200px) {
    padding: 40px 56px;
  }
  @media screen and (max-width: 768px) {
    padding: 40px 24px;
  }
  .tutorial-image {
    cursor: pointer;
    background: $neutral-gray-100;
    border: 1px solid $neutral-gray-100;
  }

  .error-dialog {
    img {
      filter: brightness(0.7);
    }
  }
}
</style>
<style lang="scss">
.video-content {
  width: 65%;
  margin: auto;
  padding-top: 0px;
  @media screen and (max-width: 1025px) {
    width: 93%;
  }
  @media screen and (max-width: 768px) {
    width: 100%;
  }
}
</style>
